// src/components/NewVehicleTabSpecs.tsx

import React from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { VehicleVariant } from '../types/NewVehicleTypes';

interface NewVehicleTabSpecsProps {
  variant?: VehicleVariant;
}

// Helper component for a single specification row
const SpecRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
  const displayValue = value && value !== '' ? value : '-';
  
  return (
    <Box 
      display="flex" 
      py={1.5} 
      sx={{ 
        borderBottom: '1px solid #f3f4f6',
        '&:last-child': { borderBottom: 'none' } 
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pr: 2 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1, color: 'text.primary' }}>
        {displayValue}
      </Typography>
    </Box>
  );
};

const NewVehicleTabSpecs: React.FC<NewVehicleTabSpecsProps> = ({ variant }) => {
  if (!variant) return null;

  const { specGroup, fuelType, transmissionType } = variant;

  // Formatting helpers
  const formatPower = () => {
    if (!specGroup.maximumPowerOutput) return '-';
    let pwr = `${specGroup.maximumPowerOutput} kW`;
    if (specGroup.powerRPM) pwr += ` @ ${specGroup.powerRPM} rpm`;
    return pwr;
  };

  const formatTorque = () => {
    if (!specGroup.torque) return '-';
    let trq = `${specGroup.torque} Nm`;
    if (specGroup.torqueRPM) trq += ` @ ${specGroup.torqueRPM} rpm`;
    return trq;
  };

  const formatWithUnit = (val: string | number | null | undefined, unit: string) => {
    return val && val !== '' ? `${val} ${unit}` : '-';
  };

  const isEV = fuelType === 'Electric' || specGroup.batteryCapacity;

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        
        {/* LEFT COLUMN */}
        <Grid size={{ xs: 12, md: 6 }}>
          
          {/* Engine & Performance Card */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Engine & Performance
            </Typography>
            <Box>
              <SpecRow label="Engine Code" value={specGroup.engineCode} />
              <SpecRow label="Engine Type" value={specGroup.engineType} />
              <SpecRow label="Displacement" value={formatWithUnit(specGroup.engineCapacity, 'cc')} />
              <SpecRow label="Max Power" value={formatPower()} />
              <SpecRow label="Max Torque" value={formatTorque()} />
              <SpecRow label="0 - 100 km/h" value={formatWithUnit(specGroup.zeroToHundred, 's')} />
              <SpecRow label="Top Speed" value={formatWithUnit(specGroup.topSpeed, 'km/h')} />
              <SpecRow label="Fuel Type" value={fuelType} />
              <SpecRow label="Fuel Tank Capacity" value={formatWithUnit(specGroup.fuelTankCapacity, 'L')} />
              <SpecRow 
                label="Fuel Consumption" 
                value={formatWithUnit(specGroup.fuelConsumption, isEV ? 'kWh/100km' : 'km/L')} 
              />
            </Box>
          </Paper>

          {/* EV & Charging Card (Conditional) */}
          {isEV && (
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                EV & Charging
              </Typography>
              <Box>
                <SpecRow label="Battery Capacity" value={formatWithUnit(specGroup.batteryCapacity, 'kWh')} />
                <SpecRow label="Driving Range" value={formatWithUnit(specGroup.drivingRange, 'km')} />
                <SpecRow label="Energy Efficiency" value={specGroup.energyEfficiency} />
                <SpecRow label="Charging Type" value={specGroup.chargingType} />
                <SpecRow label="AC Charging Time" value={specGroup.acchargingTime} />
                <SpecRow label="DC Charging Time" value={specGroup.dcchargingTime} />
              </Box>
            </Paper>
          )}

          {/* Transmission Card */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Transmission
            </Typography>
            <Box>
              <SpecRow label="Transmission Type" value={transmissionType} />
              <SpecRow label="Drive Type" value={specGroup.driveType} />
            </Box>
          </Paper>

        </Grid>

        {/* RIGHT COLUMN */}
        <Grid size={{ xs: 12, md: 6 }}>
          
          {/* Dimensions & Capacity Card */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Dimensions & Capacity
            </Typography>
            <Box>
              <SpecRow label="Length" value={formatWithUnit(specGroup.length, 'mm')} />
              <SpecRow label="Width" value={formatWithUnit(specGroup.width, 'mm')} />
              <SpecRow label="Height" value={formatWithUnit(specGroup.height, 'mm')} />
              <SpecRow label="Wheelbase" value={formatWithUnit(specGroup.wheelbase, 'mm')} />
              <SpecRow label="Min. Turning Radius" value={formatWithUnit(specGroup.turningRadius, 'm')} />
              <SpecRow label="Kerb Weight" value={formatWithUnit(specGroup.kerbWeight, 'kg')} />
              <SpecRow label="Gross Vehicle Weight" value={formatWithUnit(specGroup.grossVehicleWeight, 'kg')} />
              <SpecRow label="Payload" value={formatWithUnit(specGroup.payload, 'kg')} />
              <SpecRow label="Cargo Capacity" value={specGroup.cargoCapacity} />
              <SpecRow label="Boot Space" value={specGroup.bootSpace} />
            </Box>
          </Paper>

          {/* Chassis & Others Card */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Chassis & Others
            </Typography>
            <Box>
              <SpecRow label="Seating Capacity" value={formatWithUnit(specGroup.seatCapacity, 'Seats')} />
              <SpecRow label="Doors" value={formatWithUnit(specGroup.doors, 'Doors')} />
              <SpecRow label="Tyre Size" value={specGroup.tyreSize} />
              <SpecRow label="Wheel Size" value={formatWithUnit(specGroup.wheelSize, 'inch')} />
              <SpecRow label="Brakes" value={specGroup.brakes} />
              <SpecRow label="Suspension" value={specGroup.suspension} />
            </Box>
          </Paper>

        </Grid>

      </Grid>
    </Box>
  );
};

export default NewVehicleTabSpecs;